/**
 * User Mappers
 * @description Utility functions for mapping between different user types
 */

import {
  User,
  UserFormData,
  UserCreateRequest,
  UserUpdateRequest,
  UserRole,
} from '@/types/user.types';

/**
 * Maps UserFormData to UserCreateRequest
 */
export const toCreateRequest = (formData: UserFormData): UserCreateRequest => {
  return {
    personalInfo: {
      name: formData.personalInfo.name,
      middlename: formData.personalInfo.middlename,
      surname1: formData.personalInfo.surname1,
      surname2: formData.personalInfo.surname2,
      company: formData.personalInfo.company,
      email1: formData.personalInfo.email1,
      email2: formData.personalInfo.email2,
      phone1: formData.personalInfo.phone1,
      phone2: formData.personalInfo.phone2,
    },
    credentials: {
      username: formData.credentials.username,
      ...(formData.credentials.password ? { password: formData.credentials.password } : {}),
      requiresPasswordChange: formData.credentials.requiresPasswordChange,
    },
    role: formData.role || UserRole.USER, // Provide default
    timezone: formData.timezone,
    preferences: {
      timezone: formData.preferences.timezone,
      dateFormat: formData.preferences.dateFormat,
      timeFormat: formData.preferences.timeFormat,
      language: formData.preferences.language,
      notifications: formData.preferences.notifications,
    },
    deviceAccess: formData.deviceAccess,
    securitySettings: {
      mfaEnabled: false, // formData.securitySettings.mfaEnabled - TODO: Review this
      mfaMethod: '', // formData.securitySettings.mfaMethod - TODO: Review this
      mfaBackupCodes: [], // formData.securitySettings.mfaBackupCodes - TODO: Review this
    },
    // Subscription data
    subscription: formData.metadata?.subscription,
    subscriptionExpiryDate: formData.metadata?.subscriptionExpiryDate,
  };
};

/**
 * Maps UserFormData to UserUpdateRequest
 */
export const toUpdateRequest = (formData: UserFormData): UserUpdateRequest => {
  return {
    id: formData.id,
    personalInfo: {
      name: formData.personalInfo.name,
      middlename: formData.personalInfo.middlename,
      surname1: formData.personalInfo.surname1,
      surname2: formData.personalInfo.surname2,
      company: formData.personalInfo.company,
      email1: formData.personalInfo.email1,
      email2: formData.personalInfo.email2,
      phone1: formData.personalInfo.phone1,
      phone2: formData.personalInfo.phone2,
    },
    credentials: {
      username: formData.credentials.username,
      requiresPasswordChange: formData.credentials.requiresPasswordChange,
    },
    role: formData.role,
    timezone: formData.timezone,
    preferences: {
      timezone: formData.preferences.timezone,
      dateFormat: formData.preferences.dateFormat,
      timeFormat: formData.preferences.timeFormat,
      language: formData.preferences.language,
      notifications: formData.preferences.notifications,
    },
    deviceAccess: formData.deviceAccess,
    securitySettings: {
      mfaEnabled: false, // formData.securitySettings.mfaEnabled - TODO: Review this
      mfaMethod: '', // formData.securitySettings.mfaMethod - TODO: Review this
      mfaBackupCodes: [], // formData.securitySettings.mfaBackupCodes - TODO: Review this
    },
    // Subscription data
    subscription: formData.metadata?.subscription,
    subscriptionExpiryDate: formData.metadata?.subscriptionExpiryDate,
  };
};

/**
 * Maps User (API response) to UserFormData
 */
export const toFormData = (user: User): UserFormData => {
  return {
    id: user.id,
    personalInfo: {
      name: user.personalInfo.name,
      middlename: user.personalInfo.middlename,
      surname1: user.personalInfo.surname1,
      surname2: user.personalInfo.surname2,
      company: user.personalInfo.company,
      email1: user.personalInfo.email1,
      email2: user.personalInfo.email2,
      phone1: user.personalInfo.phone1,
      phone2: user.personalInfo.phone2,
    },
    credentials: {
      username: user.credentials.username,
      passwordLastChanged: user.credentials.passwordLastChanged,
      requiresPasswordChange: user.credentials.requiresPasswordChange,
    },
    role: user.role,
    timezone: user.timezone || 'America/Argentina/Buenos_Aires', // Default timezone if not provided
    deviceAccess: user.deviceAccess,
    preferences: {
      timezone: user.preferences.timezone || 'America/Argentina/Buenos_Aires', // Default timezone if not provided
      dateFormat: user.preferences.dateFormat,
      timeFormat: user.preferences.timeFormat,
      language: user.preferences.language,
      notifications: user.preferences.notifications,
    },
    metadata: {
      version: user.metadata?.version || 1,
      subscription: user.metadata?.subscription || 'active',
      subscriptionExpiryDate: user.metadata?.subscriptionExpiryDate,
    },
  };
};

/**
 * User mappers object for easy import
 */
export const userMappers = {
  toCreateRequest,
  toUpdateRequest,
  toFormData,
};
